// src/pages/StorytellingResourcesPage.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  Headphones,
  Youtube,
  FileText,
  Users,
  Wrench,
  GraduationCap,
  ExternalLink,
  Star,
  BookMarked,
  Mic,
  Video,
  Search,
  Filter,
  ChevronRight,
  Sparkles,
  Award
} from 'lucide-react';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import SupportLines, { SupportLinesLink } from '../components/resources/SupportLines';

const StorytellingResourcesPage = () => {
  const [activeCategory, setActiveCategory] = useState('books');
  const [searchTerm, setSearchTerm] = useState('');

  // Books - Based on brief recommendations
  const books = [
    {
      title: "The Storytelling Animal: How Stories Make Us Human",
      author: "Jonathan Gottschall",
      credentials: "Distinguished Fellow in English, Washington & Jefferson College",
      year: "2012",
      description: "Why humans are wired for story and how narratives shape everything from childhood play to religious belief to politics.",
      whyRelevant: "Foundational understanding of why storytelling matters. Connects to Module 1 (Story Landscape) and Module 2 (Why Stories Stick).",
      keyTakeaway: "Humans don't just tell stories. We live inside them. Story is the primary way we make sense of the chaos of experience.",
      modules: [1, 2],
      pageCount: 248,
      category: "Foundation",
      purchaseLink: "https://www.amazon.com/Storytelling-Animal-Stories-Make-Human/dp/0544002342"
    },
    {
      title: "The Stories We Live By: Personal Myths and the Making of the Self",
      author: "Dan McAdams",
      credentials: "Professor of Psychology, Northwestern University",
      year: "1993",
      description: "McAdams' account of narrative identity: how the stories we tell about our lives help make us who we are.",
      whyRelevant: "Our pick for narrative identity, and essential for Module 1. McAdams argues that each of us builds an identity through the life story we tell about ourselves, and that revising the story can open new possibilities.",
      keyTakeaway: "We are all storytellers, and in a sense we are the stories we tell. Identity takes the form of an internalised, evolving life story that links the remembered past with an imagined future.",
      modules: [1, 3],
      pageCount: 336,
      category: "Foundation",
      purchaseLink: "https://www.guilford.com/books/The-Stories-We-Live-By/Dan-McAdams/9781572301887"
    },
    {
      title: "Wired for Story: The Writer's Guide to Using Brain Science to Hook Readers from the Very First Sentence",
      author: "Lisa Cron",
      credentials: "Story coach and former literary agent",
      year: "2012",
      description: "A story coach's guide to what holds a reader, drawing on popular brain science.",
      whyRelevant: "Bridges Module 2 (why stories stick) and Module 5 (emotional craft). Makes the case for why specific sensory details tend to work better than abstract labels.",
      keyTakeaway: "Story is how we make sense of the world, and specific detail does more work than abstract labels.",
      modules: [2, 5],
      pageCount: 256,
      category: "Craft",
      purchaseLink: "https://www.amazon.com/Wired-Story-Writers-Science-Readers/dp/1607742454"
    },
    {
      title: "The Hero with a Thousand Faces",
      author: "Joseph Campbell",
      credentials: "Professor of Literature, Sarah Lawrence College",
      year: "1949",
      description: "Campbell's study of a pattern he found across the world's myths: the monomyth, or Hero's Journey.",
      whyRelevant: "Understanding archetypal story patterns helps you recognise the structures you're already using unconsciously. Connects to Module 3 and Module 4.",
      keyTakeaway: "Campbell argued that many of the world's myths share one underlying pattern, the monomyth. Knowing it helps you recognise structures you already use.",
      modules: [3, 4],
      pageCount: 418,
      category: "Structure",
      purchaseLink: "https://www.amazon.com/Thousand-Faces-Collected-Joseph-Campbell/dp/1577315936"
    },
    {
      title: "The Power of Moments: Why Certain Experiences Have Extraordinary Impact",
      author: "Chip Heath & Dan Heath",
      credentials: "Stanford Professor & Senior Fellow at Duke CASE",
      year: "2017",
      description: "How to create memorable experiences, and how that applies to crafting stories that stick in memory.",
      whyRelevant: "The Heath brothers' framework for memorable moments carries over well to memorable storytelling. Key for Module 2 and Module 5.",
      keyTakeaway: "Moments matter more than time. The Heaths name four elements that make a moment memorable: elevation, insight, pride and connection.",
      modules: [2, 5],
      pageCount: 320,
      category: "Craft",
      purchaseLink: "https://www.amazon.com/Power-Moments-Certain-Experiences-Extraordinary/dp/1501147765"
    },
    {
      title: "Long Story Short: The Only Storytelling Guide You'll Ever Need",
      author: "Margot Leitman",
      credentials: "Comedian and five-time Moth champion storyteller",
      year: "2015",
      description: "Practical, no-nonsense guide to crafting personal narratives for any context.",
      whyRelevant: "Bridges all modules with practical techniques for structure, emotional arc and delivery. Perfect companion to the entire curriculum.",
      keyTakeaway: "Every story needs a beginning that hooks, a middle with stakes, and an ending that delivers meaning. Everything else is decoration.",
      modules: [3, 4, 5, 7],
      pageCount: 224,
      category: "Craft",
      purchaseLink: "https://www.penguinrandomhouse.com/books/251806/long-story-short-by-margot-leitman/"
    },
    {
      title: "Daring Greatly: How the Courage to Be Vulnerable Transforms the Way We Live, Love, Parent, and Lead",
      author: "Brené Brown",
      credentials: "Research Professor, University of Houston",
      year: "2012",
      description: "Twelve years of research on vulnerability, courage and shame, distilled into practical guidance.",
      whyRelevant: "Essential foundation for Module 6 (Vulnerability Calibration). Brown argues that vulnerability is not weakness but our most accurate measure of courage, and that it requires boundaries and trust.",
      keyTakeaway: "Vulnerability is not oversharing. It's sharing with people who've earned the right to hear your story. Context and calibration matter.",
      modules: [6],
      pageCount: 320,
      category: "Vulnerability",
      purchaseLink: "https://www.amazon.com/Daring-Greatly-Courage-Vulnerable-Transforms/dp/1592408419"
    }
  ];

  // Online Courses & Training
  const courses = [
    {
      title: "Muse Storytelling: The Science of Storytelling course",
      instructor: "Patrick Moreau",
      platform: "Muse Storytelling",
      format: "Online courses, workshops, and filmmaker-focussed resources",
      description: "A storytelling course from Muse Storytelling, a film studio and school, built around what it calls the Muse Process. Developed for filmmakers, blending cinematic structure with emotional authenticity.",
      whyValuable: "In our view, one of the most practical storytelling systems for filmmakers, and it translates well to personal stories, particularly structure (Module 4), emotional arc (Module 5) and delivery (Module 7).",
      keyLessons: "Story structure isn't formula; it's architecture. Learn to build stories that land with precision whilst keeping them authentic and emotionally true, whether you're telling them at dinner or putting them on screen.",
      modules: [1, 3, 4, 5, 7],
      priceRange: "Varies (free resources to paid courses)",
      link: "https://musestorytelling.com/"
    },
    {
      title: "Storyworthy courses and workshops",
      instructor: "Matthew Dicks",
      platform: "Storyworthy",
      format: "Online courses, workshops, and membership community",
      description: "Created by Matthew Dicks, who holds the record for Moth StorySLAM wins and is a multiple GrandSLAM champion. Courses include Storytelling Foundations and 'Storyworthy for Business'. He also wrote the book Storyworthy (2018).",
      whyValuable: "Particularly strong on finding stories (Module 1), structure (Module 4) and delivery (Module 7). His 'Homework for Life' technique is a simple daily habit for noticing storyworthy moments in everyday experience.",
      keyLessons: "Every great story turns on a five-second moment of change. Telling it in the present tense puts the audience in that moment with you. The best stories come from the smallest moments, not the biggest events. 'Homework for Life' teaches you to mine your daily experience for storyworthy moments. Stakes don't need to be life-or-death; they need to be emotionally true.",
      modules: [1, 3, 4, 5, 6, 7],
      link: "https://www.storyworthymd.com/courses"
    },
    {
      title: "MothWorks Storytelling Workshops",
      instructor: "The Moth Education Team",
      platform: "The Moth",
      format: "Workshops, live events and one-to-one story development",
      description: "MothWorks runs storytelling workshops, live events and one-to-one story development for organisations; past clients include Google, Microsoft and Nike.",
      whyValuable: "From The Moth, founded in 1997. Their workshops line up with Modules 3-7: crafting and telling true personal stories that land.",
      keyLessons: "Personal stories must be true and told in first person. Every story needs stakes: something must be at risk. Structure follows a clear arc with a beginning, middle, and end that transforms you. Vulnerability creates connection, but oversharing destroys it. Practice makes performance feel spontaneous, not rehearsed.",
      modules: [3, 4, 5, 6, 7, 8],
      link: "https://www.themoth.org/workshops/companies-and-organizations"
    },
    {
      title: "Pixar in a Box: The Art of Storytelling",
      instructor: "Pete Docter, Mark Andrews, Domee Shi (Pixar directors and story artists)",
      platform: "Khan Academy",
      format: "Free online course with videos, exercises, and hands-on activities",
      year: "2017-present",
      description: "Free lessons from Pixar directors and story artists, including Pete Docter (Inside Out, Up) and Mark Andrews (Brave), made with Khan Academy. Covers character development, story structure, visual language, and storyboarding through the lens of Pixar's creative process.",
      whyValuable: "Free, and taught by Pixar's own directors and story artists. Whilst designed for animation, the principles apply to personal storytelling, particularly character (Module 1), structure (Module 4), and emotional arc (Module 5). The visual language lessons translate beautifully to sensory storytelling.",
      keyLessons: "Character wants drive story structure. Obstacles create stakes and tension. The best stories take characters (and audiences) on an emotional journey. Internal and external conflicts mirror each other. Asking 'What if?' opens up creative storytelling possibilities.",
      modules: [1, 3, 4, 5],
      priceRange: "Free",
      link: "https://www.khanacademy.org/partner-content/pixar/storytelling"
    }
  ];

  // Academic Papers & Research
  const research = [
    {
      title: "Knowledge of Family History as a Clinically Useful Index of Psychological Well-Being and Prognosis: A Brief Report",
      authors: "Marshall P. Duke, Amber Lazarus & Robyn Fivush",
      institution: "Emory University",
      year: "2008",
      journal: "Psychotherapy: Theory, Research, Practice, Training, 45(2), 268-272",
      description: "The study behind the 20-question 'Do You Know?' scale. Duke described the ascending, descending and oscillating family narratives separately (quoted in The New York Times, 2013).",
      keyFindings: "Children who knew more of their family's history had higher self-esteem, a stronger internal locus of control, better family functioning and lower anxiety. Separately, Duke has described three family narrative patterns (ascending, descending and oscillating) and called the oscillating one 'the most healthful narrative' (The New York Times, 2013). Important limits: the study is correlational, and Duke has said it is the process of families sharing stories, not the facts themselves, that seems to matter.",
      modules: [3],
      link: "https://doi.org/10.1037/0033-3204.45.2.268",
      plainLanguageSummary: "Children who know more of their family's story, struggles as well as strengths, score better on several measures of wellbeing (Duke, Lazarus & Fivush, 2008). The study is correlational, so it can't show that the telling itself causes the benefit.",
      citationFormat: "Duke, M. P., Lazarus, A., & Fivush, R. (2008). Knowledge of family history as a clinically useful index of psychological well-being and prognosis: A brief report. Psychotherapy: Theory, Research, Practice, Training, 45(2), 268-272."
    },
    {
      title: "The Psychology of Life Stories",
      authors: "Dan McAdams",
      institution: "Northwestern University",
      year: "2001",
      journal: "Review of General Psychology, 5(2), 100-122",
      description: "McAdams' review of life-story research: the idea that identity takes the form of a story, and the work linking that story's themes with wellbeing.",
      keyFindings: "McAdams reviews research linking the themes of people's life stories with their wellbeing. The redemption and contamination findings come from a separate study the same year: redemption sequences (bad turning to good) went with higher self-reported well-being, and contamination sequences (good turning to bad) with lower well-being and more depression in midlife adults (McAdams, Reynolds, Lewis, Patten & Bowman, 2001, Personality and Social Psychology Bulletin).",
      modules: [1, 3],
      link: "https://journals.sagepub.com/doi/10.1037/1089-2680.5.2.100",
      plainLanguageSummary: "How you narrate your life is linked with your mental health. These are associations: they show the two travel together, not that one causes the other.",
      citationFormat: "McAdams, D. P. (2001). The psychology of life stories. Review of General Psychology, 5(2), 100-122."
    },
    {
      title: "Brain-to-Brain Coupling: A Mechanism for Creating and Sharing a Social World",
      authors: "Uri Hasson, et al.",
      institution: "Princeton University",
      year: "2012",
      journal: "Trends in Cognitive Sciences, 16(2), 114-121",
      description: "A review of how one person's brain activity can become coupled to another's through a shared signal such as speech, including a speaker-listener storytelling study.",
      keyFindings: "In the fMRI study it describes, listeners' brain activity tracked a speaker's as they heard a real-life story. The coupling largely disappeared when the story was told in a language the listeners didn't know, and listeners with more extensive coupling understood the story better.",
      modules: [2],
      link: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3269540/",
      plainLanguageSummary: "When a listener follows your story, their brain activity tracks yours; the closer the tracking, the better they understand.",
      citationFormat: "Hasson, U., Ghazanfar, A. A., Galantucci, B., Garrod, S., & Keysers, C. (2012). Brain-to-brain coupling. Trends in Cognitive Sciences, 16(2), 114-121."
    },
    {
      title: "The Role of Transportation in the Persuasiveness of Public Narratives",
      authors: "Melanie C. Green & Timothy C. Brock",
      institution: "Ohio State University",
      year: "2000",
      journal: "Journal of Personality and Social Psychology, 79(5), 701-721",
      description: "How being absorbed in a story ('transportation') shifts readers' beliefs towards the story's view.",
      keyFindings: "Across four experiments, more transported readers held more story-consistent beliefs, rated the protagonists more favourably and noticed fewer false notes in the story. Labelling the story fact or fiction generally made no difference.",
      modules: [2, 5],
      link: "https://doi.org/10.1037/0022-3514.79.5.701",
      plainLanguageSummary: "When you are absorbed in a story you look for fewer flaws in it, and your beliefs shift towards it, even when you know it is fiction.",
      citationFormat: "Green, M. C., & Brock, T. C. (2000). The role of transportation in the persuasiveness of public narratives. Journal of Personality and Social Psychology, 79(5), 701-721."
    },
    {
      title: "Narrative Stories as Mediators for Serial Learning",
      authors: "Gordon H. Bower & Michal C. Clark",
      institution: "Stanford University",
      year: "1969",
      journal: "Psychonomic Science, 14(4), 181-182",
      description: "A 1969 memory experiment often linked to the idea that stories are remembered better than facts.",
      keyFindings: "Participants learned twelve lists of ten unrelated nouns, either by ordinary rehearsal or by inventing a story that linked the words. Immediate recall was near perfect for both groups. Final recall across all twelve lists was 93% for the story group and 13% for the rehearsal group: six to seven times as much. Worth noting the scope: this is word-list learning in a lab, not a speech or a conversation. It demonstrates the mechanism, not a guarantee about every kind of content.",
      modules: [2],
      link: "https://link.springer.com/article/10.3758/BF03332778",
      plainLanguageSummary: "Weaving words into a story made them far more durable in memory: six to seven times better recall at the end of the session. It tested word lists, not stories versus facts.",
      citationFormat: "Bower, G. H., & Clark, M. C. (1969). Narrative stories as mediators for serial learning. Psychonomic Science, 14(4), 181-182."
    },
    {
      title: "Living Into the Story: Agency and Coherence in a Longitudinal Study of Narrative Identity Development and Mental Health Over the Course of Psychotherapy",
      authors: "Jonathan M. Adler",
      institution: "Olin College of Engineering",
      year: "2012",
      journal: "Journal of Personality and Social Psychology, 102(2), 367-389",
      description: "Described by its author as the first prospective, multiwave longitudinal study of narrative identity and mental health in psychotherapy, showing that a change in how people told their story came before a change in how they felt.",
      keyFindings: "Forty-seven adults wrote personal narratives before therapy began and after every session, across 12 assessment points. Narratives were coded for agency and coherence. Agency increased over the course of therapy (coherence did not), and those changes came before the related improvements in mental health. Coming first is not proof of cause. Limits: a small sample in one clinical population.",
      modules: [1, 3],
      link: "https://pubmed.ncbi.nlm.nih.gov/21910554/",
      plainLanguageSummary: "Most research linking life stories to wellbeing can only show the two travel together. This one tracked the same people session by session and found the story shifted first: people started casting themselves as the author of their life, and their mental health improved afterwards.",
      citationFormat: "Adler, J. M. (2012). Living into the story: Agency and coherence in a longitudinal study of narrative identity development and mental health over the course of psychotherapy. Journal of Personality and Social Psychology, 102(2), 367-389."
    },
    {
      title: "The Experimental Generation of Interpersonal Closeness",
      authors: "Arthur Aron, Edward Melinat, Elaine N. Aron, Robert Darrin Vallone & Renee J. Bator",
      institution: "State University of New York at Stony Brook",
      year: "1997",
      journal: "Personality and Social Psychology Bulletin, 23(4), 363-377",
      description: "The study behind the '36 questions', and what it actually tested.",
      keyFindings: "Pairs of strangers who worked through 36 progressively deepening questions over 45 minutes reported substantially greater closeness than pairs given small talk for the same time. Two things the study is often credited with but didn't claim: it measured closeness, not love (the 'fall in love' framing came from Mandy Len Catron's 2015 New York Times Modern Love essay), and no condition tested skipping the escalation and opening with the deep questions.",
      modules: [6, 8],
      link: "https://journals.sagepub.com/doi/10.1177/0146167297234003",
      plainLanguageSummary: "Structured, mutual, gradually deepening questions bring strangers closer than small talk does. That is what was tested. The romance framing came later, from a newspaper essay.",
      citationFormat: "Aron, A., Melinat, E., Aron, E. N., Vallone, R. D., & Bator, R. J. (1997). The experimental generation of interpersonal closeness: A procedure and some preliminary findings. Personality and Social Psychology Bulletin, 23(4), 363-377."
    },
    {
      title: "The Emotional Arcs of Stories Are Dominated by Six Basic Shapes",
      authors: "Andrew J. Reagan, Lewis Mitchell, Dilan Kiley, Christopher M. Danforth & Peter Sheridan Dodds",
      institution: "University of Vermont",
      year: "2016",
      journal: "EPJ Data Science, 5(31)",
      description: "A data test of the story-shapes idea from Kurt Vonnegut's rejected master's thesis.",
      keyFindings: "Sentiment analysis across 1,327 works from Project Gutenberg found emotional arcs cluster into six core shapes, including 'man in a hole' (fall then rise) and 'Icarus' (rise then fall). Their analysis supports the core of Vonnegut's idea; his own version was a novelist's insight, not a study, and shouldn't be cited as though it were.",
      modules: [5],
      link: "https://epjdatascience.springeropen.com/articles/10.1140/epjds/s13688-016-0093-1",
      plainLanguageSummary: "Vonnegut said stories have shapes you could draw on graph paper. Chicago rejected the thesis. Nearly 70 years later a team ran the numbers on 1,327 books and found that stories do cluster into a handful of shapes, much as he had suggested.",
      citationFormat: "Reagan, A. J., Mitchell, L., Kiley, D., Danforth, C. M., & Dodds, P. S. (2016). The emotional arcs of stories are dominated by six basic shapes. EPJ Data Science, 5(31)."
    }
  ];

  // Podcasts
  const podcasts = [
    {
      title: "Conversations (ABC Radio National)",
      hosts: "Richard Fidler & Sarah Kanowski",
      region: "Australia 🇦🇺",
      description: "In-depth interviews where guests tell their life stories. In our view, one of the best long-form interview podcasts anywhere.",
      whyValuable: "Watch master interviewers create space for profound storytelling. Notice how they elicit stories through questions, silence, and genuine curiosity. Essential study for Module 8.",
      recommendedEpisodes: [
        "David Malouf on growing up in Brisbane and his life of letters",
        "Leigh Sales: the day after the worst possible day",
        "Stan Grant on what his father taught him"
      ],
      listenFor: "Interview technique, question crafting, silence and space, how stories emerge organically, vulnerability calibration in public conversation.",
      link: "https://www.abc.net.au/listen/programs/conversations",
      modules: [1, 3, 6, 8]
    },
    {
      title: "The Moth",
      hosts: "Various storytellers",
      region: "USA 🇺🇸",
      description: "Live storytelling events featuring true, personal stories told without notes. The gold standard for personal narrative performance.",
      whyValuable: "Listen to how skilled storytellers structure personal narratives, build emotional arcs, and land endings. Each story is a masterclass in modules 3, 4, 5, and 7.",
      recommendedEpisodes: [
        "Daniel Turpin: The Good and The Bad (the aftermath of a split-second decision)",
        "Or browse The Moth's story library by theme"
      ],
      listenFor: "Structure (clear beginning/middle/end), emotional arc, sensory details, vulnerability with boundaries, delivery presence, how they handle the ending.",
      link: "https://themoth.org/podcast",
      modules: [3, 4, 5, 6, 7]
    },
    {
      title: "The Moth in Melbourne",
      hosts: "Various storytellers",
      region: "Australia 🇦🇺",
      description: "The Moth runs regular StorySLAM nights in Melbourne.",
      whyValuable: "Same format as the US Moth but with Australian voices and cultural contexts. Shows how storytelling principles work across cultures.",
      recommendedEpisodes: [
        "Check The Moth's events listings for upcoming Melbourne StorySLAMs"
      ],
      listenFor: "Cultural context adaptation, Australian humour and sensibility, how universal structure works with local flavour.",
      link: "https://www.themoth.org/",
      modules: [3, 4, 5, 7]
    },
    {
      title: "This American Life",
      hosts: "Ira Glass",
      region: "USA 🇺🇸",
      description: "Narrative journalism at its finest. Each episode is a masterclass in story structure and emotional craft.",
      whyValuable: "Notice how producers structure complex stories with multiple threads. The way they use specific moments to illuminate bigger themes is directly applicable to personal storytelling.",
      recommendedEpisodes: [
        "The Giant Pool of Money (a complex story made accessible through narrative structure)",
        "Harper High School (struggle and resilience side by side)",
        "The Invention of Money (abstract concepts made concrete through specific stories)"
      ],
      listenFor: "How they structure complex narratives, the use of anecdotes vs. exposition, pacing, transitions between story threads, emotional modulation.",
      link: "https://www.thisamericanlife.org/",
      modules: [2, 3, 4, 5]
    },
    {
      title: "The Blindboy Podcast",
      hosts: "Blindboyboatclub",
      region: "Ireland 🇮🇪",
      description: "Irish storyteller blending memoir, psychology, history, and humour. Unique narrative voice and fearless vulnerability.",
      whyValuable: "Blindboy demonstrates how to weave personal stories with broader themes. His vulnerability calibration and tangential storytelling style create unexpected connections.",
      recommendedEpisodes: [
        "Look for episodes on mental health, for vulnerability with boundaries",
        "Look for episodes on history through a personal lens"
      ],
      listenFor: "Tangential storytelling that circles back, vulnerability in public, Irish oral storytelling tradition, humour as emotional regulation.",
      link: "https://shows.acast.com/blindboy",
      modules: [3, 5, 6, 7]
    },
    {
      title: "The Guilty Feminist",
      hosts: "Deborah Frances-White",
      region: "UK 🇬🇧",
      description: "Comedy podcast where personal stories blend with feminist discussion. Excellent example of vulnerability with humour.",
      whyValuable: "Watch how Frances-White and guests use personal narratives to explore bigger ideas. In our reading, the 'I'm a feminist but...' format swings between ideals and confessions.",
      recommendedEpisodes: [
        "Look for episodes on storytelling and narrative craft",
        "Listen to how guests' stories range in how much they disclose"
      ],
      listenFor: "Using humour to handle vulnerability, story as vehicle for ideas, British vs American storytelling styles, group storytelling dynamics.",
      link: "https://guiltyfeminist.com/",
      modules: [3, 6, 7]
    },
    {
      title: "StoryCorps",
      hosts: "Community storytellers",
      region: "USA 🇺🇸",
      description: "Authentic conversations between loved ones, preserved for history. Raw, unpolished, deeply moving.",
      whyValuable: "These aren't performed stories; they're real conversations. Listen for how people naturally structure meaningful moments when they're not performing. Module 8 in action.",
      recommendedEpisodes: [
        "Browse the StoryCorps podcast archive"
      ],
      listenFor: "How people naturally tell meaningful stories in conversation, how listening creates space, the power of silence and presence, authentic vulnerability.",
      link: "https://storycorps.org/podcast/",
      modules: [1, 6, 8]
    },
    {
      title: "All in the Mind",
      hosts: "Sana Qadar",
      region: "Australia 🇦🇺",
      description: "ABC's psychology and neuroscience podcast. Regularly features episodes on memory, narrative, and identity.",
      whyValuable: "Australian perspective on the neuroscience of storytelling. Qadar makes research accessible through personal stories and interviews.",
      recommendedEpisodes: [
        "Look for episodes on memory",
        "Look for episodes on identity and narrative",
        "Look for episodes on trauma and story"
      ],
      listenFor: "How research becomes story, Australian research and researchers, accessibility without dumbing down, interviewing scientists.",
      link: "https://www.abc.net.au/listen/programs/allinthemind",
      modules: [1, 2]
    },
    {
      title: "The Documentary Podcast (BBC World Service)",
      hosts: "Various",
      region: "UK 🇬🇧",
      description: "BBC World Service's documentary series. Excellent examples of narrative journalism from international perspectives.",
      whyValuable: "See how stories work across cultures. BBC producers excel at finding the human story within bigger events. International range of voices and narrative styles.",
      recommendedEpisodes: [
        "Look for personal history documentaries (memoir as journalism)",
        "Look for episodes on culture and identity (cross-cultural narrative)"
      ],
      listenFor: "International storytelling styles, cultural context, how to tell big stories through personal lens, British narrative sensibility.",
      link: "https://www.bbc.co.uk/programmes/p02nq0lx/episodes/downloads",
      modules: [1, 4, 5]
    },
    {
      title: "Hidden Brain",
      hosts: "Shankar Vedantam",
      region: "USA 🇺🇸",
      description: "Psychology and neuroscience through narrative. Many episodes directly address storytelling, memory, and identity.",
      whyValuable: "Vedantam uses stories to explain research, then explains the research behind why stories work. Meta-learning about storytelling through storytelling.",
      recommendedEpisodes: [
        "You 2.0: Change Your Story, Change Your Life (Jonathan Adler on narrative identity)",
        "The Story of Your Life (rewriting our personal narratives)",
        "Creating God (Azim Shariff on religion as a cultural innovation)"
      ],
      listenFor: "How research is made accessible through narrative, the integration of data and story, pacing between exposition and narrative.",
      link: "https://hiddenbrain.org/",
      modules: [1, 2]
    },
    {
      title: "Earshot (ABC Radio National)",
      hosts: "Various producers",
      region: "Australia 🇦🇺",
      description: "ABC Radio National's documentary series of intimate first-person stories (archive).",
      whyValuable: "In our view, Australian audio storytelling at its best. Shows how personal story and documentary blend. Excellent narrative structure and sound design.",
      recommendedEpisodes: [
        "Browse the archive for first-person stories"
      ],
      listenFor: "Australian narrative voice, sound design as storytelling, pacing and structure.",
      link: "https://www.abc.net.au/listen/programs/earshot",
      modules: [1, 3, 4, 5]
    },
    {
      title: "On Being with Krista Tippett",
      hosts: "Krista Tippett",
      region: "USA 🇺🇸",
      description: "Deep, meaningful conversations about life, meaning, and spirituality. Tippett is a master interviewer who elicits profound stories.",
      whyValuable: "Watch a master of Module 8 in action. Tippett's questions create space for guests to tell stories they didn't know they had. Perfect example of story listening.",
      recommendedEpisodes: [
        "David Whyte: The Conversational Nature of Reality (story as meaning-making)",
        "Brené Brown: The Courage to Be Vulnerable (vulnerability research applied)",
        "Ta-Nehisi Coates: Imagining a New America (personal narrative and broader context)"
      ],
      listenFor: "How questions elicit stories, how silence creates space, how listening without fixing allows processing, the calibration of follow-up questions.",
      link: "https://onbeing.org/series/podcast/",
      modules: [6, 8]
    },
    {
      title: "The Philosopher's Zone",
      hosts: "David Rutledge",
      region: "Australia 🇦🇺",
      description: "ABC philosophy podcast that often explores narrative, identity, and the stories we tell about meaning.",
      whyValuable: "Philosophy meets storytelling. Explores the deeper questions about why stories matter and how they construct meaning and identity.",
      recommendedEpisodes: [
        "Look for episodes on narrative identity and life stories",
        "Look for episodes on memory and the self",
        "Look for episodes on truth and fiction in narrative"
      ],
      listenFor: "Theoretical frameworks for storytelling, why stories matter philosophically, Australian academic voices, accessible philosophy through examples.",
      link: "https://www.abc.net.au/listen/programs/philosopherszone",
      modules: [1, 2]
    },
    {
      title: "Radiolab",
      hosts: "Lulu Miller & Latif Nasser",
      region: "USA 🇺🇸",
      description: "Science storytelling with incredible sound design and narrative structure. Each episode is a lesson in making complex ideas compelling.",
      whyValuable: "Notice how they structure stories about abstract concepts, use specific moments to illuminate big ideas, and employ sensory details (especially sound) to create emotional resonance.",
      recommendedEpisodes: [
        "Memory and Forgetting (how narrative constructs memory)",
        "Loops (story structure and the power of repetition)",
        "Choice (making meaning through narrative framing)"
      ],
      listenFor: "Narrative structure for complex topics, sensory details (especially auditory), emotional arc in science storytelling, context adaptation for different audiences.",
      link: "https://radiolab.org/",
      modules: [2, 4, 5]
    }
  ];

  // Video Resources
  const videos = [
    {
      title: "Brené Brown: The Power of Vulnerability",
      platform: "TED",
      duration: "20 minutes",
      description: "Brown's TEDxHouston talk (2010) on vulnerability, shame and connection. Essential viewing for Module 6.",
      whyWatch: "Brown demonstrates vulnerability whilst teaching about it. Notice her storytelling technique, use of humour, and how she calibrates personal disclosure for a large audience.",
      keyLessons: "Brown describes vulnerability as the core of shame and fear, and also the birthplace of joy, creativity, belonging and love.",
      link: "https://www.ted.com/talks/brene_brown_the_power_of_vulnerability",
      modules: [6]
    },
    {
      title: "Andrew Stanton: The Clues to a Great Story",
      platform: "TED",
      duration: "19 minutes",
      description: "Pixar storyteller reveals the principles behind Toy Story, Finding Nemo, and WALL-E, all applicable to personal narrative.",
      whyWatch: "Stanton breaks down universal story principles. His idea of giving the audience two plus two rather than four (let them put things together instead of explaining everything) is gold for personal storytelling.",
      keyLessons: "Make me care. Invoke wonder. The audience wants to work for their meal: give them two plus two, not four.",
      link: "https://www.ted.com/talks/andrew_stanton_the_clues_to_a_great_story",
      modules: [3, 4, 5]
    },
    {
      title: "Chimamanda Ngozi Adichie: The Danger of a Single Story",
      platform: "TED",
      duration: "19 minutes",
      description: "How single narratives create stereotypes and incomplete understanding. Our link to Module 3: tell struggle and strength together.",
      whyWatch: "Adichie shows why we need complete stories, both struggle and strength. In our reading, it's Module 3's oscillating narrative principle applied to culture and identity.",
      keyLessons: "The single story creates stereotypes, and the problem with stereotypes is not that they are untrue, but that they are incomplete. Stories matter. Many stories matter.",
      link: "https://www.ted.com/talks/chimamanda_ngozi_adichie_the_danger_of_a_single_story",
      modules: [1, 3]
    },
    {
      title: "Julian Treasure: How to Speak So That People Want to Listen",
      platform: "TED",
      duration: "10 minutes",
      description: "Voice, delivery, and presence. Practical techniques for Module 7.",
      whyWatch: "Treasure breaks down the vocal and physical components of effective speaking. Watch how he demonstrates each principle as he teaches it.",
      keyLessons: "HAIL: Honesty, Authenticity, Integrity, Love. Plus vocal toolbox (register, timbre, prosody, pace, pitch, volume) and the power of silence.",
      link: "https://www.ted.com/talks/julian_treasure_how_to_speak_so_that_people_want_to_listen",
      modules: [7]
    },
    {
      title: "The Moth YouTube Channel",
      platform: "YouTube",
      duration: "Various",
      description: "Moth stories filmed live. A deep library of personal storytelling examples.",
      whyWatch: "See storytelling in action. Notice structure, delivery, emotional arc, how different storytellers handle vulnerability, and audience response.",
      keyLessons: "Personal storytelling is a performative art but it must feel authentic. The best stories balance rehearsal with spontaneity.",
      link: "https://www.youtube.com/@themoth",
      modules: [3, 4, 5, 6, 7]
    }
  ];

  const categories = [
    { id: 'books', label: 'Books', icon: BookOpen, count: books.length },
    { id: 'courses', label: 'Courses', icon: GraduationCap, count: courses.length },
    { id: 'research', label: 'Research Papers', icon: FileText, count: research.length },
    { id: 'podcasts', label: 'Podcasts', icon: Headphones, count: podcasts.length },
    { id: 'videos', label: 'Videos', icon: Youtube, count: videos.length }
  ];

  const getActiveResources = () => {
    let resources = [];
    switch (activeCategory) {
      case 'books': resources = books; break;
      case 'courses': resources = courses; break;
      case 'research': resources = research; break;
      case 'podcasts': resources = podcasts; break;
      case 'videos': resources = videos; break;
      default: resources = books;
    }

    if (!searchTerm) return resources;

    return resources.filter(resource =>
      resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (resource.author && resource.author.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (resource.instructor && resource.instructor.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (resource.platform && resource.platform.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (resource.description && resource.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const resources = getActiveResources();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 md:px-8 pb-8 pt-40">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-block"
          >
            <img
              src="/images/resources.png"
              alt="Storytelling Resources"
              className="w-44 h-44 object-contain rounded-2xl mx-auto"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </motion.div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Storytelling Resources
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Curated books, courses, research, podcasts, and videos to deepen your storytelling mastery
          </p>
          <p className="text-sm text-slate-400 max-w-2xl mx-auto mt-4">
            Need someone to talk to? <SupportLinesLink>Support lines</SupportLinesLink> for Australia,
            New Zealand, India, the UK, Ireland, Canada and the US are at the end of this page.
          </p>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-purple-500"
            />
          </div>
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                    activeCategory === category.id
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/30'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border border-slate-700'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{category.label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activeCategory === category.id
                      ? 'bg-white/20'
                      : 'bg-slate-700'
                  }`}>
                    {category.count}
                  </span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Resources Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="grid md:grid-cols-2 gap-6"
          >
            {resources.map((resource, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                {activeCategory === 'books' && (
                  <Card className="p-6 h-full hover:border-purple-500/30 transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-1">
                          {resource.title}
                        </h3>
                        <p className="text-slate-400 text-sm mb-1">
                          {resource.author}
                        </p>
                        {resource.credentials && (
                          <p className="text-slate-500 text-xs mb-2">
                            {resource.credentials}
                          </p>
                        )}
                      </div>
                    </div>

                    <p className="text-slate-300 text-sm mb-4">
                      {resource.description}
                    </p>

                    <div className="space-y-3 mb-4">
                      <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                        <p className="text-xs font-semibold text-purple-300 uppercase mb-1">
                          Why it's relevant
                        </p>
                        <p className="text-slate-300 text-sm">
                          {resource.whyRelevant}
                        </p>
                      </div>

                      <div className="bg-teal-500/10 border border-teal-500/20 rounded-lg p-3">
                        <p className="text-xs font-semibold text-teal-300 uppercase mb-1">
                          What we take from it
                        </p>
                        {/* Our summary of the book's argument, not a quotation from it —
                            so it is deliberately NOT rendered inside quote marks. */}
                        <p className="text-slate-300 text-sm">
                          {resource.keyTakeaway}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                      <div className="flex items-center gap-2">
                        <BookMarked className="w-4 h-4 text-slate-400" />
                        <span className="text-xs text-slate-400">
                          Modules: {resource.modules.join(', ')}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => window.open(resource.purchaseLink, '_blank')}
                      >
                        <ExternalLink className="w-3 h-3 mr-2" />
                        View Book
                      </Button>
                    </div>
                  </Card>
                )}

                {activeCategory === 'courses' && (
                  <Card className="p-6 h-full hover:border-purple-500/30 transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-1">
                          {resource.title}
                        </h3>
                        <p className="text-slate-400 text-sm mb-1">
                          {resource.instructor} • {resource.platform}
                        </p>
                        {resource.format && (
                          <p className="text-slate-500 text-xs mb-2">
                            {resource.format}
                          </p>
                        )}
                      </div>
                    </div>

                    <p className="text-slate-300 text-sm mb-4">
                      {resource.description}
                    </p>

                    <div className="space-y-3 mb-4">
                      <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                        <p className="text-xs font-semibold text-purple-300 uppercase mb-1">
                          Why it's valuable
                        </p>
                        <p className="text-slate-300 text-sm">
                          {resource.whyValuable}
                        </p>
                      </div>

                      <div className="bg-teal-500/10 border border-teal-500/20 rounded-lg p-3">
                        <p className="text-xs font-semibold text-teal-300 uppercase mb-1">
                          What you'll take away
                        </p>
                        {/* Summary of the material in our words. Some lines paraphrase the
                            speaker closely, so these are NOT presented as verbatim quotes. */}
                        <p className="text-slate-300 text-sm">
                          {resource.keyLessons}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                      <div className="flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 text-slate-400" />
                        <span className="text-xs text-slate-400">
                          Modules: {resource.modules.join(', ')}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => window.open(resource.link, '_blank')}
                      >
                        <ExternalLink className="w-3 h-3 mr-2" />
                        Visit Course
                      </Button>
                    </div>
                  </Card>
                )}

                {activeCategory === 'research' && (
                  <Card className="p-6 h-full hover:border-purple-500/30 transition-all">
                    <h3 className="text-xl font-bold text-white mb-2">
                      {resource.title}
                    </h3>
                    <p className="text-slate-400 text-sm mb-1">
                      {resource.authors} • {resource.institution}
                    </p>
                    <p className="text-slate-500 text-xs mb-4">
                      {resource.journal || resource.year}
                    </p>

                    <div className="space-y-3 mb-4">
                      <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase mb-1">
                          In Plain Language
                        </p>
                        <p className="text-slate-300 text-sm">
                          {resource.plainLanguageSummary}
                        </p>
                      </div>

                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                        <p className="text-xs font-semibold text-emerald-300 uppercase mb-1">
                          Key Findings
                        </p>
                        <p className="text-slate-300 text-sm">
                          {resource.keyFindings}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <span className="text-xs text-slate-400">
                          Modules: {resource.modules.join(', ')}
                        </span>
                      </div>
                      {resource.link && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => window.open(resource.link, '_blank')}
                        >
                          <ExternalLink className="w-3 h-3 mr-2" />
                          Read Paper
                        </Button>
                      )}
                    </div>
                  </Card>
                )}

                {activeCategory === 'podcasts' && (
                  <Card className="p-6 h-full hover:border-purple-500/30 transition-all">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Headphones className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-1">
                          {resource.title}
                        </h3>
                        <p className="text-slate-400 text-sm">
                          {resource.hosts}
                        </p>
                      </div>
                    </div>

                    <p className="text-slate-300 text-sm mb-4">
                      {resource.description}
                    </p>

                    <div className="space-y-3 mb-4">
                      <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3">
                        <p className="text-xs font-semibold text-indigo-300 uppercase mb-1">
                          Why It's Valuable
                        </p>
                        <p className="text-slate-300 text-sm">
                          {resource.whyValuable}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-slate-400 uppercase mb-2">
                          Where to Start
                        </p>
                        <ul className="space-y-1">
                          {resource.recommendedEpisodes.slice(0, 3).map((episode, i) => (
                            <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                              <ChevronRight className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                              <span>{episode}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-slate-800/50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-slate-400 uppercase mb-1">
                          What to Listen For
                        </p>
                        <p className="text-slate-300 text-sm">
                          {resource.listenFor}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                      <div className="flex items-center gap-2">
                        <Mic className="w-4 h-4 text-slate-400" />
                        <span className="text-xs text-slate-400">
                          Modules: {resource.modules.join(', ')}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => window.open(resource.link, '_blank')}
                      >
                        <ExternalLink className="w-3 h-3 mr-2" />
                        Listen
                      </Button>
                    </div>
                  </Card>
                )}

                {activeCategory === 'videos' && (
                  <Card className="p-6 h-full hover:border-purple-500/30 transition-all">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Video className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-1">
                          {resource.title}
                        </h3>
                        <p className="text-slate-400 text-sm">
                          {resource.platform} • {resource.duration}
                        </p>
                      </div>
                    </div>

                    <p className="text-slate-300 text-sm mb-4">
                      {resource.description}
                    </p>

                    <div className="space-y-3 mb-4">
                      <div className="bg-rose-500/10 border border-rose-500/20 rounded-lg p-3">
                        <p className="text-xs font-semibold text-rose-300 uppercase mb-1">
                          Why Watch
                        </p>
                        <p className="text-slate-300 text-sm">
                          {resource.whyWatch}
                        </p>
                      </div>

                      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                        <p className="text-xs font-semibold text-amber-300 uppercase mb-1">
                          Key Lessons
                        </p>
                        <p className="text-slate-300 text-sm">
                          {resource.keyLessons}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                      <div className="flex items-center gap-2">
                        <Youtube className="w-4 h-4 text-slate-400" />
                        <span className="text-xs text-slate-400">
                          Modules: {resource.modules.join(', ')}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => window.open(resource.link, '_blank')}
                      >
                        <ExternalLink className="w-3 h-3 mr-2" />
                        Watch
                      </Button>
                    </div>
                  </Card>
                )}
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {resources.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-400 text-lg">No resources found matching "{searchTerm}"</p>
          </div>
        )}

        <SupportLines />
      </div>
    </div>
  );
};

export default StorytellingResourcesPage;
